import { CalendarDays, MapPin, BarChart3, FileText, MessageCircle, ShieldCheck, ShieldAlert } from "lucide-react";

export function bookingPkg(q) {
  const selId = q.acceptance?.selected_package_id || q.selected_package_id;
  return (q.packages || []).find((p) => p.id === selId) || null;
}

export function fmtGBP(n) {
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
}

export function fmtDate(dateStr) {
  if (!dateStr) return "Date TBC";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

export function BookingRow({ q, onDetails, onAnalytics, onWhatsApp }) {
  const acc = q.acceptance || {};
  const pkg = bookingPkg(q);
  const paid = acc.deposit_paid?.amount;
  const addonsTotal = (acc.addons || []).reduce((s, a) => s + Number(a.price), 0);
  const balance = pkg && paid ? Math.max(0, Number(pkg.price) + addonsTotal - Number(paid)) : null;

  return (
    <div
      data-testid={`booking-row-${q.slug}`}
      className="bg-white border border-[#E5E3DB] p-6 md:p-7 flex flex-col lg:flex-row lg:items-center gap-6"
    >
      <div className="lg:w-[30%]">
        <div className="font-display text-2xl text-[#1F1E1D] leading-tight">
          {acc.partner1_full_name || q.partner1_name} &amp; {acc.partner2_full_name || q.partner2_name}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#5C5A56]">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5 text-[#A9927D]" strokeWidth={1.5} />
            {fmtDate(q.wedding_date || acc.wedding_date)}
          </span>
          {(q.venue_name || acc.wedding_venue) && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#A9927D]" strokeWidth={1.5} />
              {q.venue_name || acc.wedding_venue}
            </span>
          )}
        </div>
        {acc.accepted_at && (
          <div data-testid={`booking-accepted-date-${q.slug}`} className="mt-1.5 text-xs text-[#8A8782]">
            Booked{" "}
            {new Date(acc.accepted_at).toLocaleDateString("en-GB", {
              day: "numeric", month: "short", year: "numeric",
            })}
          </div>
        )}
      </div>

      <div className="lg:w-[28%] text-sm">
        <div className="text-[10px] tracking-overline uppercase text-[#8A8782] mb-1">Package</div>
        <div className="text-[#1F1E1D] font-medium">{pkg ? pkg.name : "Unknown"}</div>
        {pkg && <div className="text-[#5C5A56]">{fmtGBP(pkg.price)}</div>}
      </div>

      <div className="lg:w-[22%] text-sm">
        <div className="text-[10px] tracking-overline uppercase text-[#8A8782] mb-1">Deposit</div>
        {paid ? (
          <>
            <div className="text-[#2E5E3A] font-medium">{fmtGBP(paid)} paid</div>
            {balance !== null && <div className="text-[#5C5A56]">{fmtGBP(balance)} remaining</div>}
          </>
        ) : (
          <div className="text-[#5C5A56]">No online deposit</div>
        )}
        <div className="mt-2">
          {acc.terms_agreed_at ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-overline uppercase font-medium text-[#2E5E3A]">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Terms agreed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-overline uppercase font-medium text-[#9A6B15]">
              <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} /> No terms on record
            </span>
          )}
        </div>
      </div>

      <div className="lg:flex-1 flex flex-wrap items-center justify-start lg:justify-end gap-2">
        <button
          onClick={() => onDetails(q)}
          data-testid={`booking-details-${q.slug}`}
          className="inline-flex items-center gap-1.5 bg-[#2F2E2E] text-white px-4 py-2.5 text-[10px] tracking-overline uppercase font-medium hover:bg-[#454341] transition-colors"
        >
          <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Contract
        </button>
        <button
          onClick={() => onWhatsApp(q)}
          data-testid={`booking-whatsapp-${q.slug}`}
          className="inline-flex items-center gap-1.5 border border-[#D1CEC2] text-[#5C5A56] hover:text-[#1F1E1D] hover:border-[#2F2E2E] px-4 py-2.5 text-[10px] tracking-overline uppercase font-medium transition-colors"
        >
          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp
        </button>
        <button
          onClick={() => onAnalytics(q)}
          data-testid={`booking-analytics-${q.slug}`}
          className="inline-flex items-center gap-1.5 border border-[#D1CEC2] text-[#5C5A56] hover:text-[#1F1E1D] hover:border-[#2F2E2E] px-4 py-2.5 text-[10px] tracking-overline uppercase font-medium transition-colors"
        >
          <BarChart3 className="w-3.5 h-3.5" strokeWidth={1.5} /> Analytics
        </button>
      </div>
    </div>
  );
}
